import type { EnvironmentsSchemaCampaign, EnvironmentsSchemaSegment } from '@customerio/hydra-ui-api';
import type { DataIndexAttributeUsage, DataIndexObjectAttributeUsage } from '@/services/db/data-index/entity';
import type { DataIndexUsage, DataIndexUsageKey } from './types';

/**
 * Counting and splitting the things a data-index item is used by.
 *
 * Archived segments and campaigns still count as usage — they are what stops
 * an attribute being deleted — but they are listed apart from the live ones,
 * so most surfaces need the split rather than the raw arrays.
 */

/** The campaign state that means archived. */
const CAMPAIGN_STATE_ARCHIVED = 'archived';

export function getActiveSegments(
  segments: EnvironmentsSchemaSegment[] | null | undefined,
): EnvironmentsSchemaSegment[] {
  return (segments ?? []).filter((segment) => !segment.archived);
}

export function getArchivedSegments(
  segments: EnvironmentsSchemaSegment[] | null | undefined,
): EnvironmentsSchemaSegment[] {
  return (segments ?? []).filter((segment) => segment.archived);
}

export function getActiveCampaigns(
  campaigns: EnvironmentsSchemaCampaign[] | null | undefined,
): EnvironmentsSchemaCampaign[] {
  return (campaigns ?? []).filter((campaign) => campaign.state !== CAMPAIGN_STATE_ARCHIVED);
}

export function getArchivedCampaigns(
  campaigns: EnvironmentsSchemaCampaign[] | null | undefined,
): EnvironmentsSchemaCampaign[] {
  return (campaigns ?? []).filter((campaign) => campaign.state === CAMPAIGN_STATE_ARCHIVED);
}

/** How many of one kind of thing use this item. */
export function getUsageCount(usage: DataIndexUsage, key: DataIndexUsageKey): number {
  return usage[key]?.length ?? 0;
}

/**
 * How many things use an item, when all the caller has is counts.
 *
 * A detail read answers with the records themselves; the usage endpoint a
 * list uses answers with counts only, and an object's attributes cannot
 * appear in content at all, so that answer is narrower again. Reading all
 * three through one vocabulary is what lets the delete gate name the same
 * blocker wherever it is asked.
 */
export type DataIndexUsageCounts = DataIndexAttributeUsage | DataIndexObjectAttributeUsage;

const COUNT_READERS = {
  segments: (counts) => counts.segment_count,
  campaigns: (counts) => counts.campaign_count,
  newsletters: (counts) => counts.newsletter_count,
  templates: (counts) => ('template_count' in counts ? counts.template_count : 0),
  snippets: (counts) => ('snippet_count' in counts ? counts.snippet_count : 0),
  layouts: (counts) => ('layout_count' in counts ? counts.layout_count : 0),
  design_studio: (counts) => ('design_studio_count' in counts ? counts.design_studio_count : 0),
} satisfies Record<DataIndexUsageKey, (counts: DataIndexUsageCounts) => number>;

/** A usage lookup over counts. Nothing known means nothing uses it. */
export function usageCountsLookup(counts: DataIndexUsageCounts | undefined): (key: DataIndexUsageKey) => number {
  return (key) => (counts == null ? 0 : COUNT_READERS[key](counts));
}

/** The same lookup over a detail read's records. */
export function usageRecordsLookup(usage: DataIndexUsage): (key: DataIndexUsageKey) => number {
  return (key) => getUsageCount(usage, key);
}

export function getTotalSegmentCount(usage: DataIndexUsage): number {
  return getUsageCount(usage, 'segments');
}

/** Which usage surfaces a given item shows, and therefore what its badge counts. */
export type UsageTabScope = {
  /**
   * Templates, snippets and layouts. Off for object and relationship
   * attributes when the workspace does not have that flag, in which case the
   * badge must not count views the tab does not render.
   */
  withTemplatesSnippetsLayouts?: boolean;
  /**
   * Design Studio, which only profile attributes carry — events, object and
   * relationship attributes have no such field, so callers opt in.
   */
  withDesignStudio?: boolean;
};

const ALWAYS_COUNTED: DataIndexUsageKey[] = ['segments', 'campaigns', 'newsletters'];
const CONTENT_KEYS: DataIndexUsageKey[] = ['templates', 'snippets', 'layouts'];

/** The number on the Usage tab: everything the tab will actually render. */
export function getTotalCountForUsageTab(usage: DataIndexUsage, scope: UsageTabScope = {}): number {
  const { withTemplatesSnippetsLayouts = true, withDesignStudio = false } = scope;

  const keys = [
    ...ALWAYS_COUNTED,
    ...(withTemplatesSnippetsLayouts ? CONTENT_KEYS : []),
    ...(withDesignStudio ? (['design_studio'] as const) : []),
  ];

  return keys.reduce((total, key) => total + getUsageCount(usage, key), 0);
}
